import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import VideoHero from '@/components/promo/VideoHero';
import SepLine from '@/components/promo/SepLine';
import LetsTalk from '@/components/promo/LetsTalk';
import { promoApi, type Partner } from '@/lib/api/promoApi';

function PlatformIcons({ platforms }: { platforms: string[] }) {
  return (
    <span className="partner-platforms">
      {platforms.map((p) => {
        switch (p) {
          case 'PC': return <i className="fa fa-windows" key="pc" />;
          case 'Mobile': return <><i className="fa fa-android" key="m-a" /><i className="fa fa-apple" key="m-i" /></>;
          case 'Android': return <i className="fa fa-android" key="android" />;
          case 'iOS': return <i className="fa fa-apple" key="ios" />;
          case 'Console': return <i className="fa fa-gamepad" key="console" />;
          default: return null;
        }
      })}
    </span>
  );
}

export default function Partners() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [genreFilter, setGenreFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');

  useEffect(() => {
    promoApi.getPartners()
      .then(setPartners)
      .catch((err) => console.error('[Partners]', err))
      .finally(() => setLoading(false));
  }, []);

  const genres = useMemo(
    () => [...new Set(partners.map((p) => p.genre).filter(Boolean))].sort() as string[],
    [partners],
  );

  const platforms = useMemo(
    () => ['PC', 'Console', 'Android', 'iOS'].filter(
      (pl) => partners.some((p) => p.platforms?.includes(pl)),
    ),
    [partners],
  );

  const filtered = useMemo(() => {
    let result = partners;
    if (genreFilter) result = result.filter((p) => p.genre === genreFilter);
    if (platformFilter) result = result.filter((p) => p.platforms?.includes(platformFilter));
    return result;
  }, [partners, genreFilter, platformFilter]);

  return (
    <div id="partners">
      <VideoHero videoSrc="/assets/videos/case_studies.mp4" imageSrc="/assets/images/casestudies.png">
        <h1>SHOW AND TELL</h1>
        <h2>winning stories from<br />our clients</h2>
        <SepLine />
        <div style={{ marginTop: 20 }}>
          <Link to="/login" className="btn-gc">JOIN US</Link>
        </div>
      </VideoHero>

      <div className="page-content" style={{ paddingBottom: 0 }}>
        <div style={{ padding: '50px 20px 0' }}>
          <h3 style={{ textAlign: 'center', fontSize: 22, letterSpacing: 4, color: '#333' }}>Partners</h3>
        </div>

        <SepLine />

        {/* Filters */}
        {(genres.length > 0 || platforms.length > 0) && (
          <div className="partners-filters">
            <select value={genreFilter} onChange={(e) => setGenreFilter(e.target.value)}>
              <option value="">Genre ...</option>
              {genres.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
              <option value="">Platform ...</option>
              {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        )}

        {/* Partner Cards */}
        {loading ? (
          <p style={{ textAlign: 'center', padding: 60, color: '#999' }}>Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="nothing-found">Nothing found</div>
        ) : (
          <div className="partners-grid">
            {filtered.map((partner) => (
              <div key={partner._id} className="partner-outer-wrapper">
                <div className="partner-wrapper">
                  {partner.genre && partner.genre !== 'Other' && (
                    <span className="partner-genre">{partner.genre}</span>
                  )}
                  {partner.platforms && partner.platforms.length > 0 && (
                    <PlatformIcons platforms={partner.platforms} />
                  )}
                  <div className="partner-image-wrapper">
                    <img
                      className="partner-image"
                      src={partner.mainPicture?.[0]?.url}
                      alt={partner.game}
                      loading="lazy"
                    />
                    <div className="partner-title">{partner.game}</div>
                  </div>
                  <div className="partner-content">
                    <h4>{partner.company}</h4>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <SepLine invisible />
        <LetsTalk />
      </div>
    </div>
  );
}
